import { Injectable, ComponentRef, ApplicationRef, Injector, RendererFactory2, ComponentFactoryResolver, Type, inject, DOCUMENT } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { Subscription, Subject, Observable } from 'rxjs';
import { PopupCountService } from './count.service';
import { filter } from 'rxjs/operators';
import { PopupBaseComponent } from '../class/popup.class';

import { WindowService } from '@shared/window';

@Injectable()
export class PopupService {
    private applicationRef = inject(ApplicationRef);
    private injector = inject(Injector);
    private rendererFactory2 = inject(RendererFactory2);
    private renderer2 = this.rendererFactory2.createRenderer(null, null);
    private componentFactoryResolver = inject(ComponentFactoryResolver);
    private router = inject(Router);
    private popupCountService = inject(PopupCountService);
    private document = inject(DOCUMENT);
    private windowService = inject(WindowService);

    /**
     * Simple Popup
     */
    popupComponentRef?: ComponentRef<PopupBaseComponent>;

    /**
     * Closing Subject
     */
    private closingSubject = new Subject<string>();

    /**
     * Subscription
     */
    private subscriptionClose?: Subscription;

    /**
     * Subscription
     */
    private subscriptionRouter?: Subscription;

    /**
     * Is Open Flag
     */
    private isOpen = false;

    /**
     * Opens popup with component, attaches it to ApplicationRef and renders at the end of <body>
     */
    open(compnent: Type<PopupBaseComponent>, data?: unknown): void {
        if (this.isOpen) {
            return;
        }

        // enable scroll
        if (window) {
            this.isOpen = true;
            // add open popup
            this.popupCountService.add();
            // resolve and create component
            this.popupComponentRef = this.componentFactoryResolver.resolveComponentFactory(compnent).create(this.injector);
            // set data
            if (data) {
                this.popupComponentRef.instance.setData(data);
            }
            // attache to applicationRef
            this.applicationRef.attachView(this.popupComponentRef.hostView);
            // render at the end of body
            this.renderer2.appendChild(this.document.querySelector('body'), this.popupComponentRef.location.nativeElement);

            this.bodyScrollLock();
        }
        // subscribe to close
        this.subscriptionClose = this.popupComponentRef?.instance.closeEvent.subscribe((reason: string) => this.close(reason));
        // subscribe to route change
        this.subscriptionRouter = this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => this.close('navigation-start'));
    }

    /**
     * Close Event
     */
    get closeEvent(): Observable<string> {
        return this.closingSubject.asObservable();
    }

    /**
     * Close - is fired always when popup closes
     */
    close(reason: string): void {
        if (!this.isOpen) {
            return;
        }
        this.isOpen = false;
        // unsubscribe
        this.subscriptionClose?.unsubscribe();
        this.subscriptionRouter?.unsubscribe();
        // enable scroll

        this.bodyScrollUnlock();

        // destroy component ref
        this.popupComponentRef?.destroy();
        // subtract popup count
        this.popupCountService.remove();
        // pass close reason
        this.closingSubject.next(reason);
    }

    private bodyScrollLock = () => {
        this.renderer2.setStyle(document.body, 'padding-right', `${this.windowService.scrollbarWidth}px`);
        this.renderer2.addClass(document.body, 'lock');
    };

    private bodyScrollUnlock = () => {
        this.renderer2.removeStyle(document.body, 'padding-right');
        this.renderer2.removeClass(document.body, 'lock');
    };
}
